import $ from "jquery";

declare const gon: any;

interface PostBookingEventData {
    product_code?: string;
    product_name?: string;
    quantity?: number;
    duration_ms?: number;
    error_type?: string;
    message?: string;
}

/** Offers on the booking page. The value is the product code. */
const ANCILLARY_SELECTOR: string = "[data-ancillary]";
const CLICK_EVENTS: string[] = ["click", "touchstart"];

/** Where each flow renders the options it loaded over ajax. */
const OPTIONS_CONTAINERS: { [productCode: string]: string } = {
    BAGGAGE: "#baggage-content",
    SEATING: "#seating-content",
    UPGRADES: "#upgrades-content"
};

const clickedAt: { [productCode: string]: number } = {};

/**
 * Sends a post-booking event, correlated by order_id. No-ops unless
 * gon.post_booking is set. The session and all money are added server side.
 */
export function logPostBookingEvent(event: string, data: PostBookingEventData = {}): void {
    const booking = gon.post_booking;

    if (!booking) {
        return;
    }

    $.post("/post_booking_events", {
        order_id: booking.order_id,
        midoffice_id: booking.midoffice_id,
        search_id: booking.search_id,
        device: booking.device,
        event: event,
        url: document.location.href,
        product_code: data.product_code,
        product_name: data.product_name,
        quantity: data.quantity,
        duration_ms: data.duration_ms,
        error_type: data.error_type,
        message: data.message
    });
}

function productCodeOf(target: Element): string {
    if (!target || !target.closest) {
        return null;
    }

    const offer = target.closest(ANCILLARY_SELECTOR) as HTMLElement;
    return offer ? offer.getAttribute("data-ancillary") : null;
}

function trackClick(event: Event): void {
    const productCode = productCodeOf(event.target as Element);

    // One event per product per page load.
    if (!productCode || clickedAt[productCode]) {
        return;
    }

    clickedAt[productCode] = Date.now();
    logPostBookingEvent("ancillary_clicked", { product_code: productCode });
}

function countOptions(productCode: string): number {
    const element = document.querySelector(OPTIONS_CONTAINERS[productCode]);
    if (!element) {
        return 0;
    }

    const inputs = element.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    // A seat map carries one hidden input per seat, driven by clicking the seat
    // itself, so only visible controls are real options.
    let visible: number = 0;
    for (let i: number = 0; i < inputs.length; i++) {
        if ((inputs[i] as HTMLElement).offsetParent !== null) {
            visible++;
        }
    }

    return visible;
}

function trackOptionsShown(event: Event): void {
    const productCode = productCodeOf(event.target as Element);
    // Other offers are remote links too - only the ones with an options panel
    // have anything to report here.
    if (!productCode || !OPTIONS_CONTAINERS[productCode]) {
        return;
    }

    logPostBookingEvent("ancillary_options_shown", {
        product_code: productCode,
        quantity: countOptions(productCode),
        duration_ms: clickedAt[productCode] ? Date.now() - clickedAt[productCode] : 0
    });
}

function trackOptionsError(event: Event): void {
    const productCode = productCodeOf(event.target as Element);
    if (!productCode || !OPTIONS_CONTAINERS[productCode]) {
        return;
    }

    logPostBookingEvent("technical_error", {
        product_code: productCode,
        error_type: "options_load_failed",
        message: "Loading the ancillary options failed"
    });
}

/**
 * Reports which offer the customer pressed. Native listeners, so this works
 * before any jQuery is on the page - mobile loads its Sprockets bundle only
 * after the webpack pack has run.
 */
export function initPostBookingTracking(): void {
    CLICK_EVENTS.forEach((eventName: string): void => {
        document.addEventListener(eventName, trackClick, { passive: true });
    });
}

/**
 * Reports whether the bags/seats/upgrades options made it on screen and how
 * long that took. rails-ujs fires jQuery events, and those only reach handlers
 * bound through the same jQuery - desktop runs it from webpack, mobile from
 * Sprockets.
 *
 * @param jq the jQuery instance rails-ujs was loaded against
 */
export function initAncillaryAjaxTracking(jq: any): void {
    if (!jq) {
        return;
    }

    jq(document).on("ajax:success", ANCILLARY_SELECTOR, trackOptionsShown);
    jq(document).on("ajax:error", ANCILLARY_SELECTOR, trackOptionsError);
}
